import { useEffect, useState } from "react";
import { GoogleAuthProvider, signInWithPopup, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { auth, call } from "../firebase";
import { friendly } from "../errors";
import { askText } from "../dialog";

const KEY = "ahg.emailForSignIn";

export default function Login() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  const isLink = isSignInWithEmailLink(auth, window.location.href);
  const [linking, setLinking] = useState(isLink);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // L34: khóa nút sau khi gửi để không ra 2 thư (link thư đầu sẽ bị vô hiệu)
  useEffect(() => { if (cooldown <= 0) return; const t = setTimeout(() => setCooldown((c) => c - 1), 1000); return () => clearTimeout(t); }, [cooldown]);

  // Quay lại từ link đăng nhập trong email (L6): xử lý xong thì tải lại trang sạch để vào thẳng Hub
  useEffect(() => {
    if (!isLink) return;
    let saved = ""; try { saved = localStorage.getItem(KEY) ?? ""; } catch { /* trình duyệt chặn lưu */ }
    // Mở link trên thiết bị khác với nơi gửi → hỏi lại email bằng hộp trong trang
    (saved ? Promise.resolve(saved) : askText({ title: "Xác nhận email đăng nhập", confirm: "Đăng nhập",
      message: "Bạn đang mở link trên một thiết bị hoặc trình duyệt khác. Nhập lại email đã dùng để nhận link.",
      input: { label: "Email", type: "email", placeholder: "ten@gmail.com", required: true } }))
      .then((e) => signInWithEmailLink(auth, (e ?? "").trim(), window.location.href))
      .then(() => { try { localStorage.removeItem(KEY); } catch { /* bỏ qua */ } window.location.replace("/"); })
      .catch((err: { code?: string }) => {
        // Link đã dùng rồi nhưng vẫn đang đăng nhập (vd bấm link lần 2) → vào thẳng
        if (auth.currentUser) return window.location.replace("/");
        setLinking(false);
        setMsg({ err: err.code === "auth/invalid-action-code" || err.code === "auth/expired-action-code"
          ? "Link đăng nhập đã hết hạn hoặc đã dùng. Vui lòng gửi link mới."
          : err.code === "auth/invalid-email" ? "Email nhập lại không khớp với email đã nhận link." : friendly(err) });
      });
  }, [isLink]);

  const google = () => signInWithPopup(auth, new GoogleAuthProvider()).catch((e: { code?: string }) => {
    if (e.code === "auth/popup-closed-by-user" || e.code === "auth/cancelled-popup-request") return;
    setMsg({ err: e.code === "auth/popup-blocked" ? "Trình duyệt chặn cửa sổ đăng nhập Google. Hãy cho phép cửa sổ bật lên rồi thử lại." : friendly(e) });
  });
  const link = async () => {
    if (sending || cooldown > 0) return;
    const to = email.trim().toLowerCase();
    setSending(true); setMsg({});
    try {
      // L33: Hub tự gửi thư tiếng Việt từ hộp thư của công ty; máy thử nghiệm chưa có email thì dùng cách của Firebase
      const r = await call<object, { ok?: boolean; fallback?: boolean; wait?: number }>("sendLoginLink")({ email: to, origin: window.location.origin });
      if (r.fallback) await sendSignInLinkToEmail(auth, to, { url: window.location.origin, handleCodeInApp: true });
      try { localStorage.setItem(KEY, to); } catch { /* bỏ qua */ }
      setCooldown(r.wait ?? 60);
      setMsg({ ok: `Nếu ${to} đã được cấp quyền, thư "Link đăng nhập" từ AHG Hub sẽ đến trong 1–2 phút. Mở thư trên chính thiết bị này để vào Hub. Không thấy thư thì xem mục Thư rác (Spam).` });
    } catch (e) { setMsg({ err: friendly(e) }); }
    setSending(false);
  };

  const shell = (body: React.ReactNode) => (
    <div className="login">
      <div className="loginBrand">
        <div className="loginLogo"><img src="/brand/ahg-logo.png" alt="An Hưng Group – An Nhiên Hưng Nghiệp" /></div>
        <div className="loginTitle">AHG Hub</div>
        <div className="loginSub">Kho tài liệu &amp; tài sản An Hưng Group</div>
      </div>
      <div className="card loginCard">{body}</div>
      <div className="loginFoot">An Hưng Group · Lưu hành nội bộ</div>
    </div>
  );

  if (linking) return shell(<p style={{ margin: 0, textAlign: "center" }}>Đang đăng nhập vào AHG Hub…</p>);

  return shell(<>
    <h2 className="loginH">Đăng nhập</h2>
    <button className="gbtn" onClick={google}>
      <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      Tiếp tục với Google
    </button>
    <div className="orLine"><span>hoặc nhận link qua email</span></div>
    <label className="mailField" htmlFor="em">
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
      <input id="em" type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email của bạn" aria-label="Email" />
    </label>
    <button className="btn primary" disabled={!email.includes("@") || sending || cooldown > 0} onClick={link}>
      {sending ? "Đang gửi…" : cooldown > 0 ? `Gửi lại sau ${cooldown} giây` : "Gửi link đăng nhập"}</button>
    {msg.ok && <div className="ok">{msg.ok}</div>}
    {msg.err && <div className="err">{msg.err}</div>}
    <p className="loginHelp">Chỉ người đã được Quản trị cấp quyền mới vào được Hub.</p>
  </>);
}
